import {call, put, select} from "typed-redux-saga";
import {DrawPredictorApi} from "apps/DRAW_PREDICTOR/modules/utils";
import {
	fetchFanFactsJSONFailed,
	fetchFanFactsJSONSuccess,
	fetchFanSentimentsJsonFailed,
	fetchFanSentimentsJsonSuccess,
	fetchQualifierGroupsJSONFailed,
	fetchQualifierGroupsJSONSuccess,
	fetchQualifierSquadsJSONFailed,
	fetchQualifierSquadsJSONSuccess,
	fetchSettingsJSONFailed,
	fetchSettingsJSONSuccess,
	fetchSquadsJsonFailed,
	fetchSquadsJsonSuccess,
	fetchSupportedCountriesJsonFailed,
	fetchSupportedCountriesJsonSuccess,
	initPreDraw,
	setFullLockout,
} from "apps/DRAW_PREDICTOR/modules/actions";
import {setFanSentimentsQuestions} from "apps/DRAW_PREDICTOR/modules/actions/fanSentiments";
import {find, range} from "lodash";
import {getFanSentimentsQuestions, getPreDrawPicks} from "apps/DRAW_PREDICTOR/modules/selectors";
import {IFanDraw, IQualifierSquad} from "apps/DRAW_PREDICTOR/modules/reducers";

export const fetchSquadsJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.squads);
		yield* put(fetchSquadsJsonSuccess(result.squads));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchSquadsJsonFailed(message));
	}
};

export const fetchSupportedCountriesJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.supported_countries);
		yield* put(fetchSupportedCountriesJsonSuccess(result));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchSupportedCountriesJsonFailed(message));
	}
};

export const fetchFanSentimentsJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.fan_sentiment_questions);
		yield* put(fetchFanSentimentsJsonSuccess(result));

		const questions = yield* select(getFanSentimentsQuestions);
		if (questions) {
			yield* put(
				setFanSentimentsQuestions([
					...questions.map((question: IFanDraw) => ({
						...question,
						answers: range(question.answerNumber).fill(0),
					})),
				])
			);
		}
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchFanSentimentsJsonFailed(message));
	}
};

export const fetchSettingsJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.settings);
		if (result.isLocked) {
			yield* put(setFullLockout());
		}

		yield* put(fetchSettingsJSONSuccess(result));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchSettingsJSONFailed(message));
	}
};

export const fetchFanFactsJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.fan_facts);

		yield* put(fetchFanFactsJSONSuccess(result));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchFanFactsJSONFailed(message));
	}
};

export const fetchQualifierGroupsJSONSaga = function* () {
	try {
		const result = yield* call(DrawPredictorApi.JSON.qualifierGroups);

		yield* put(
			fetchQualifierGroupsJSONSuccess(
				result.groups.map((group) => ({
					...group,
					pre_state_enabled: result.pre_state_enabled,
				}))
			)
		);
		const savedPicks = yield* select(getPreDrawPicks);

		const preDrawPicks = result.groups.map((group) => {
			const savedGroup = find(savedPicks, ({id}) => id === group.id);

			if (savedGroup) {
				return {
					id: group.id,
					allPicked: Boolean(savedGroup.allPicked),
					questions: group.questions.map((question) => {
						const saved = find(savedGroup.questions, ({id}) => id === question.id);
						return {
							id: question.id,
							selected: saved ? saved.selected : null,
						};
					}),
				};
			}

			return {
				id: group.id,
				allPicked: false,
				questions: group.questions.map((question) => {
					return {
						id: question.id,
						selected: null,
					};
				}),
			};
		});
		yield* put(initPreDraw(preDrawPicks));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchQualifierGroupsJSONFailed(message));
	}
};

export const fetchQualifierSquadsJSONSaga = function* () {
	try {
		const result: IQualifierSquad[] = yield* call(DrawPredictorApi.JSON.qualifierSquads);

		yield* put(fetchQualifierSquadsJSONSuccess(result));
	} catch (err) {
		const {message} = err as Error;
		yield* put(fetchQualifierSquadsJSONFailed(message));
	}
};
